// Persian formatting helpers shared by the API, admin and website.
// Prices, counts and dates use Persian digits; codes (SKU, phone, OTP, tracking) stay Latin.

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';

export function toFaDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)]!);
}

export function toEnDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(AR_DIGITS.indexOf(d)));
}

/** 1250000 -> "۱٬۲۵۰٬۰۰۰" */
export function formatNumber(value: number): string {
  const sign = value < 0 ? '-' : '';
  const grouped = Math.abs(Math.round(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '٬');
  return sign + toFaDigits(grouped);
}

/** 1250000 -> "۱٬۲۵۰٬۰۰۰ تومان" */
export function formatToman(value: number): string {
  return `${formatNumber(value)} تومان`;
}

/** 20 -> "٪۲۰" */
export function formatPercent(value: number): string {
  return `٪${toFaDigits(Math.round(value))}`;
}

const dateFmt = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { year: 'numeric', month: 'long', day: 'numeric' });
const dateShortFmt = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { year: '2-digit', month: '2-digit', day: '2-digit' });
const timeFmt = new Intl.DateTimeFormat('fa-IR', { hour: '2-digit', minute: '2-digit', hour12: false });

/** Jalali date, e.g. "۲۸ شهریور ۱۴۰۵" */
export function formatDate(value: string | number | Date): string {
  return dateFmt.format(new Date(value));
}
export function formatDateShort(value: string | number | Date): string {
  return dateShortFmt.format(new Date(value));
}
export function formatDateTime(value: string | number | Date): string {
  const d = new Date(value);
  return `${dateFmt.format(d)}، ساعت ${timeFmt.format(d)}`;
}

/** Human "time ago" in Persian for feeds and activity. */
export function formatRelative(value: string | number | Date, now: Date = new Date()): string {
  const diff = (now.getTime() - new Date(value).getTime()) / 1000;
  if (diff < 60) return 'همین حالا';
  if (diff < 3600) return `${toFaDigits(Math.floor(diff / 60))} دقیقه پیش`;
  if (diff < 86400) return `${toFaDigits(Math.floor(diff / 3600))} ساعت پیش`;
  if (diff < 86400 * 7) return `${toFaDigits(Math.floor(diff / 86400))} روز پیش`;
  return formatDate(value);
}

/** Normalise Iranian mobile numbers to 09xxxxxxxxx, or null when invalid. */
export function normalizeMobile(input: string): string | null {
  let s = toEnDigits(input).replace(/[\s\-()]/g, '');
  if (s.startsWith('+98')) s = '0' + s.slice(3);
  else if (s.startsWith('0098')) s = '0' + s.slice(4);
  else if (s.startsWith('98') && s.length === 12) s = '0' + s.slice(2);
  else if (s.startsWith('9') && s.length === 10) s = '0' + s;
  return /^09\d{9}$/.test(s) ? s : null;
}

/** Search normalisation: Arabic -> Persian letters, strip diacritics and tatweel, unify digits and spacing. */
export function normalizeSearch(input: string): string {
  return toEnDigits(input)
    .replace(/[يى]/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/[ۀة]/g, 'ه')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ؤ/g, 'و')
    .replace(/[ً-ٰٟـ]/g, '')
    .replace(/‌/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** URL slug that keeps Persian letters: "مانتو کتان" -> "مانتو-کتان". */
export function slugify(input: string): string {
  return normalizeSearch(input)
    .replace(/[^a-z0-9؀-ۿ\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
